import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  ActivatedRoute,
  Router,
  RouterModule,
} from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PriceHistoryComponent } from '../../../../shared/components/price-history/price-history.component';
import { ProductService } from '../../../../shared/services/catalog/product.service';
import {
  AdditionalProduct,
  ADDITIONAL_PRODUCT_TYPES,
  StoreAdditionalProductSchema,
} from '../../../../shared/models/catalog/additional-product.model';
import { Price } from '../../../../shared/models/catalog/price.model';
import { AdditionalProductService } from '../../../../shared/services/catalog/additional-product.service';
import { applyZodErrors } from '../../../../shared/util/zod-form-errors';

@Component({
  selector: 'app-additional-product-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    TranslateModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    MultiSelectModule,
    CheckboxModule,
    ToastModule,
    ConfirmDialogModule,
    PriceHistoryComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './additional-product-detail.component.html',
  styleUrl: './additional-product-detail.component.scss',
})
export class AdditionalProductDetailComponent implements OnInit {
  service = inject(AdditionalProductService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #fb = inject(FormBuilder);

  model = signal<AdditionalProduct | null>(null);
  isEditMode = signal(false);
  loading = signal(false);
  submitting = signal(false);

  prices = computed<Price[]>(() => {
    const m = this.model();
    return Array.isArray(m?.prices) ? (m.prices as Price[]) : [];
  });

  ownerId = computed<number | null>(() => this.model()?.id ?? null);

  typeOptions = ADDITIONAL_PRODUCT_TYPES.map((t) => ({
    value: t,
    label: `catalog.additionalProducts.types.${t}`,
  }));

  productService = inject(ProductService);

  form: FormGroup = this.#fb.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    description: [null as string | null],
    type: ['fixed' as string, [Validators.required]],
    is_universal: [false as boolean],
    product_ids: [[] as number[]],
  });

  ngOnInit(): void {
    this.productService.getAll();
    const idParam = this.#route.snapshot.paramMap.get('id');
    if (idParam && idParam !== 'new') {
      this.isEditMode.set(true);
      this.#loadModel(Number(idParam));
    }
  }

  #loadModel(id: number) {
    this.loading.set(true);
    this.service.getById(id).subscribe({
      next: (response) => {
        this.loading.set(false);
        const item = response.data;
        this.model.set(item);
        this.form.patchValue({
          title: item.title,
          description: item.description,
          type: item.type,
          is_universal: item.is_universal ?? false,
          product_ids: item.linked_product_ids ?? [],
        });
      },
      error: (e: { status?: number }) => {
        this.loading.set(false);
        if (e?.status === 404) {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translate.instant('label.errorHeader'),
            detail: this.#translate.instant(
              'catalog.additionalProducts.notFound',
            ),
          });
          this.#router.navigate(['/catalog/additional-products']);
        } else {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translate.instant('label.errorHeader'),
            detail: this.#translate.instant(
              'catalog.additionalProducts.loadError',
            ),
          });
        }
      },
    });
  }

  onSubmit() {
    if (this.submitting()) return;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const parsed = StoreAdditionalProductSchema.safeParse({
      title: raw.title,
      description: raw.description,
      type: raw.type,
      is_universal: !!raw.is_universal,
      product_ids: Array.isArray(raw.product_ids) ? (raw.product_ids as number[]) : [],
    });
    if (!parsed.success) {
      applyZodErrors(parsed.error, this.form);
      return;
    }

    this.submitting.set(true);
    const editing = this.model();

    const call$ = editing
      ? this.service.update(editing.id, parsed.data)
      : this.service.create(parsed.data);

    call$.subscribe({
      next: (response) => {
        this.submitting.set(false);
        this.model.set(response.data);
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant(
            editing
              ? 'catalog.additionalProducts.updated'
              : 'catalog.additionalProducts.created',
          ),
        });
        if (!editing) {
          this.isEditMode.set(true);
          this.#router.navigate([
            '/catalog/additional-products',
            response.data.id,
          ]);
        }
      },
      error: (e: unknown) => {
        this.submitting.set(false);
        this.#applyServerErrors(e);
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant(
            editing
              ? 'catalog.additionalProducts.updateError'
              : 'catalog.additionalProducts.createError',
          ),
        });
      },
    });
  }

  onDelete(event: Event) {
    const item = this.model();
    if (!item) return;
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant(
        'catalog.additionalProducts.deleteConfirmHeader',
      ),
      message: this.#translate.instant(
        'catalog.additionalProducts.deleteConfirmMessage',
      ),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.service.delete(item.id).subscribe({
          next: () => {
            this.#messageService.add({
              severity: 'success',
              summary: this.#translate.instant('label.successHeader'),
              detail: this.#translate.instant(
                'catalog.additionalProducts.deleted',
              ),
            });
            this.#router.navigate(['/catalog/additional-products']);
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translate.instant('label.errorHeader'),
              detail: this.#translate.instant(
                'catalog.additionalProducts.deleteError',
              ),
            });
          },
        });
      },
    });
  }

  onCancel() {
    this.#router.navigate(['/catalog/additional-products']);
  }

  reload() {
    const item = this.model();
    if (item) this.#loadModel(item.id);
  }

  #applyServerErrors(error: unknown) {
    const errBag = (error as { error?: { errors?: Record<string, string[]> } })
      ?.error?.errors;
    if (!errBag) return;
    for (const key of Object.keys(errBag)) {
      const control = this.form.get(key);
      if (control) {
        control.setErrors({ server: { message: errBag[key][0] } });
        control.markAsTouched();
      }
    }
  }
}
